import './Login.css';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, isAuthenticated } from '../services/authService';

/* 자동 로그인을 허용할 진입 경로.
 * seoro.ai.kr/mes_seoro 의 데모 버튼이 ?from=mes_seoro 를 붙여서 보낸다.
 * document.referrer 로는 판별할 수 없다 —
 *   (1) 그쪽 링크가 window.open(..., 'noopener,noreferrer') 라 referrer 자체가 안 오고
 *   (2) 오더라도 브라우저 기본 정책상 경로 없이 origin 만 전달돼 /mes_seoro 를 구분할 수 없다.
 * 다른 진입점(전시 QR 등)을 추가하려면 이 배열에 값만 넣으면 된다. */
const AUTO_LOGIN_SOURCES = ['mes_seoro'];
const DEMO_ACCOUNT = { email: 'demo@seoro.ai.kr', password: 'demo1234' };

function Login() {
    const nav = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // 지정된 진입 경로(?from=...)로 들어온 경우에만 데모 계정으로 자동 로그인한다.
    // 주소를 직접 입력해 들어오면 평소대로 로그인 화면을 보여준다.
    useEffect(() => {
        const from = new URLSearchParams(window.location.search).get('from');
        if (!AUTO_LOGIN_SOURCES.includes(from)) return;   // 직접 접속이면 평소 로그인 화면

        // 이미 로그인돼 있으면 다시 로그인하지 않고 바로 들어간다.
        if (isAuthenticated()) {
            window.history.replaceState({}, '', '/login');
            nav('/home', { replace: true });
            return;
        }

        let cancelled = false;

        const autoLogin = async () => {
            try {
                setLoading(true);
                await login(DEMO_ACCOUNT.email, DEMO_ACCOUNT.password, true);
                if (cancelled) return;
                // 주소창에서 from 을 지운다. 안 지우면 로그아웃해도 곧바로 다시 자동 로그인된다.
                window.history.replaceState({}, '', '/login');
                nav('/home', { replace: true });
            } catch (err) {
                if (cancelled) return;
                console.error('Auto login failed:', err);
                setError('자동 로그인에 실패했습니다.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        autoLogin();
        return () => { cancelled = true; };
    }, [nav]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password, rememberMe);
            nav('/home');
        } catch (err) {
            setError(err.message || '로그인에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = (e) => {
        e.preventDefault();
        alert('비밀번호 찾기는 관리자에게 문의하세요.\n\n관리자 이메일: skm@seoro.ai.kr');
    };

    const handleContactAdmin = (e) => {
        e.preventDefault();
        alert('계정 생성은 관리자에게 문의하세요.\n\n관리자 이메일: skm@seoro.ai.kr');
    };

    return ( 
        <div className="login-page">
            <div className="login-card">
                {/* Header Section */}
                <div className="login-header">
                    <p className="login-subtitle-top">Injection Molding Monitoring System</p>
                    <h1 className="login-title">사출 통합 관제 시스템</h1>
                    <div className="subtitle-divider"></div>
                    <p className="login-subtitle-kr">관리자 로그인</p>
                </div>

                {/* Login Form */}
                <form className="login-form" onSubmit={handleLogin}>
                    {error && (
                        <div style={{
                            padding: '12px',
                            marginBottom: '16px',
                            backgroundColor: '#fee',
                            color: '#c33',
                            borderRadius: '8px',
                            fontSize: '14px',
                            whiteSpace: 'pre-line'
                        }}>
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email" className="form-label">이메일</label>
                        <div className="input-container">
                            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                                type="email"
                                id="email"
                                className="form-input"
                                placeholder="admin@seoro.ai.kr"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password" className="form-label">비밀번호</label>
                        <div className="input-container">
                            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                <circle cx="12" cy="16" r="1" fill="currentColor"/>
                                <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                className="form-input"
                                placeholder="••••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            {showPassword ? (
                                <svg className="password-toggle" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={togglePasswordVisibility}>
                                    <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : (
                                <svg className="password-toggle" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={togglePasswordVisibility}>
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5.93 20 1 12 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4C18.07 4 23 12 23 12A18.5 18.5 0 0 1 20.1 19.76L9.9 4.24Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </div>
                    </div>

                    <div className="form-options">
                        <label className="checkbox-container">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                disabled={loading}
                            />
                            <span className="checkmark"></span>
                            로그인 상태 유지
                        </label>
                        <a href="#" className="forgot-password" onClick={handleForgotPassword}>비밀번호 찾기</a>
                    </div>

                    <button
                        type="submit"
                        className="login-button"
                        disabled={loading}
                        style={{ opacity: loading ? 0.7 : 1 }}
                    >
                        {loading ? '로그인 중...' : '로그인'}
                    </button>
                </form>

                {/* Footer */}
                <div className="login-footer">
                    <p>계정이 없으신가요? <a href="#" onClick={handleContactAdmin}>관리자에게 문의</a></p>
                </div>
            </div>

            {/* Copyright */}
            <div className="copyright">
                <p>Copyright © Seoro Corp. All Rights Reserved.</p>
            </div>
        </div>
    )
}

export default Login;